import { Collection, Snack } from "@tblabs/truffle";
import moment from "moment";
import { BasketItemType } from "../Models/Basket/BasketItemType";
import { RawRentBasketItem } from "../Models/Basket/RawRentBasketItem";
import { PaymentCategory } from "../Models/Payment/PaymentCategory";
import { DateTimeProvider } from "./DateTimeProvider";
import { DispatchTimeCalculator } from "./DispatchTimeCalculator";
import { Step } from "../Steps/Step/Step";
import { DeliveryForm } from "../Models/DeliveryForm";
import { Ticket } from "../Models/Ticket";
import { PackageToCustomerStepState } from "../Steps/PackageToCustomer/Models/PackageToCustomerStepState";
import { TrainingStepState } from "../Steps/Training/Models/TrainingStepState";
import { PersonalPickupStepState } from "../Steps/PersonalPickup/Models/PersonalPickupStepState";
import { PackageFromCustomerStepState } from "../Steps/PackageFromCustomer/Models/PackageFromCustomerStepState";
import { ReturnForm } from "../Models/ReturnForm";
import { UsageStepState } from "../Steps/Usage/Models/UsageStepState";
import { PersonalReturnStepState } from "../Steps/PersonalReturn/Models/PersonalReturnStepState";
import { StepFactory } from "./StepFactory";
import { StepType } from "../Steps/Step/StepType";
import { AddToBasketStepRawData } from "../Steps/AddToBasket/Models/AddToBasketStepRawData";
import { PaymentStepRawData } from "../Steps/Payment/Models/PaymentStepRawData";
import { PersonalPickupStepRawData } from "../Steps/PersonalPickup/Models/PersonalPickupStepRawData";
import { PackageToCustomerStepRawData } from "../Steps/PackageToCustomer/Models/PackageToCustomerStepRawData";
import { TrainingStepRawData } from "../Steps/Training/Models/TrainingStepRawData";
import { UsageStepRawData } from "../Steps/Usage/Models/UsageStepRawData";
import { PackageFromCustomerStepRawData } from "../Steps/PackageFromCustomer/Models/PackageFromCustomerStepRawData";
import { PersonalReturnStepRawData } from "../Steps/PersonalReturn/Models/PersonalReturnStepRawData";
import { ManaPostalDeliveryStepRawData } from "../Steps/ManaPostalDelivery/Models/ManaPostalDeliveryStepRawData";
import { InvoiceStepRawData } from "../Steps/Invoice/Models/InvoiceStepRawData";
import { InvoiceStepState } from "../Steps/Invoice/Models/InvoiceStepState";
import { TextFormatter } from "../Utils/TextFormatter";
import { PaymentForm } from "../Models/Payment/PaymentForm";


export class StepsPlanner
{
    private steps = new Collection<Step<any>>();

    private Log(...message: string[]): void
    {
        // console.log(`[StepsPlanner]`, ...message);
    }

    constructor(private _factory: StepFactory, private btcValueInPln?: number)
    { }

    public Plan(ticket: Ticket): Step<any>[]
    {
        if (this.IsAnythingToRent(ticket))
            this.WhenRent(ticket);
        else if (this.IsAnythingToSale(ticket))
            this.WhenSale(ticket);
        else
            // throw new Error("Ticket has no basket items");
            return [];

        if (ticket.Invoice)
        {
            this.AddInvoice({
                State: InvoiceStepState.Preparing,
                CompanyName: ticket.Invoice.CompanyName,
                CompanyAddress: ticket.Invoice.CompanyAddress,
                CompanyNIP: ticket.Invoice.CompanyNIP,
                DeliveryDeadline: moment(this.EndOfRent(ticket)).add(14, 'day').toDate(),
            });
        }

        return this.steps.Items;
    }

    public WhenSale(ticket: Ticket): void
    {
        this.Log("Sale");

        // 🛒 Koszyk
        this.AddAddToBasket({
            CreatedAt: ticket.Meta.CreatedAt,
            BasketItems: ticket.BasketList,
        });

        // 💰 Płatność gotówką
        if ([PaymentForm.Cash, PaymentForm.CashAtDelivery].includes(ticket.BasketPaymentForm))
        {
            // 🐤 Odbiór osobisty
            if (this.IsPersonalDelivery(ticket))
            {
                this.AddPersonalPickup({
                    State: PersonalPickupStepState.PrePlanned,
                    MeetingPlace: ticket.PickupPlace,
                    PlannedDispatchTime: this.CalcDispatchTime(ticket),
                    BasketSum: this.BasketSum(ticket),
                    DepositSum: this.DepositSum(ticket),
                    Training: ticket.WithTraining,
                    CustomerInfo: this.CustomerInfo(ticket),
                });
            }
            // 📦 Wysyłka
            else if (this.IsPackageDelivery(ticket))
            {
                this.AddPackageToCustomer(ticket, {
                    PaymentAtDelivery: this.BasketSum(ticket),
                });

                // 🎓 Szkolenie
                this.AddTraining({
                    State: TrainingStepState.NotPlannedYet
                });

                this.AddManaPostalDelivery({
                    DeliveryDeadline: moment(this.CalcDispatchTime(ticket)).add(14, 'day').toDate(),
                    Amount: this.BasketSum(ticket),
                });
            }
            else throw new Error(`Unknown delivery form "${ticket.Delivery}"`);
        }
        // 💳 Płatność przelewem
        else if (this.IsTransferable(ticket.BasketPaymentForm))
        {
            this.AddPayment(ticket, {
                Category: PaymentCategory.Basket,
                Title: this.TicketId(ticket),
                Amount: this.BasketSum(ticket),
                Form: ticket.BasketPaymentForm,
                Deadline: moment(DateTimeProvider.Now).add(3, 'days').hour(12).minutes(0).seconds(0).milliseconds(0).toDate(),
            });

            // 🐤 Odbiór osobisty
            if (this.IsPersonalDelivery(ticket))
            {
                this.AddPersonalPickup({
                    State: PersonalPickupStepState.PrePlanned,
                    MeetingPlace: ticket.PickupPlace,
                    PlannedDispatchTime: this.CalcDispatchTime(ticket),
                    BasketSum: 0,
                    DepositSum: 0,
                    Training: ticket.WithTraining,
                    CustomerInfo: this.CustomerInfo(ticket),
                });
            }
            // 📦 Wysyłka
            else if (this.IsPackageDelivery(ticket))
            {
                this.AddPackageToCustomer(ticket, {
                    PaymentAtDelivery: 0,
                });

                // 🎓 Szkolenie
                this.AddTraining({
                    State: TrainingStepState.NotPlannedYet
                });
            }
            else throw new Error(`Unknown delivery form "${ticket.Delivery}"`);
        }
    }

    public WhenRent(ticket: Ticket): void
    {
        this.Log("Rent");

        // 🛒 Koszyk
        this.AddAddToBasket({
            CreatedAt: ticket.Meta.CreatedAt,
            BasketItems: ticket.BasketList
        });

        // 💰 Kaucja gotówką
        if (this.IsDepositWithCash(ticket))
        {
            this.Log("Deposit with Cash");

            // 💰 Koszyk gotówką
            if (this.IsBasketWithCash(ticket))
            {
                this.WhenDepositWithCashAndBasketWithCash(ticket);
            }
            // 💳 Koszyk przelewem
            else if (this.IsTransferable(ticket.BasketPaymentForm))
            {
                throw new Error("You can't pay deposit with transfer when basket is paid with cash");
            }
            else throw new Error(`Unknown basket payment form "${ticket.BasketPaymentForm}"`);
        }
        // 💳 Kaucja przelewem
        else if (this.IsDepositTransferable(ticket))
        {
            // 💰 Koszyk gotówką
            if (this.IsBasketWithCash(ticket) || this.IsBasketWithCashAtDelivery(ticket))
            {
                this.WhenDepositWithTransferAndBasketWithCash(ticket);
            }
            // 💳 Koszyk przelewem
            else if (this.IsTransferable(ticket.BasketPaymentForm))
            {
                this.WhenDepositWithTransferAndBasketWithTransfer(ticket);
            }
            else throw new Error(`Unknown basket payment form "${ticket.BasketPaymentForm}"`);
        }
    }

    private IsAnythingToSale(ticket: Ticket): boolean
    {
        return ticket.BasketList.some(x => x.Type == BasketItemType.Sale);
    }

    private IsBasketWithCashAtDelivery(ticket: Ticket): boolean
    {
        return ticket.BasketPaymentForm === PaymentForm.CashAtDelivery;
    }

    public WhenDepositWithTransferAndBasketWithTransfer(ticket: Ticket): void
    {
        this.Log("Deposit with Transfer and Basket with Transfer");

        this.AddPayment(ticket, {
            Category: PaymentCategory.DepositAndBasket,
            Title: this.TicketId(ticket),
            Amount: this.DepositSum(ticket) + this.BasketSum(ticket),
            Form: ticket.DepositPaymentForm,
            Deadline: this.CalcPaymentDeadline(ticket),
        });

        if (this.IsPersonalDelivery(ticket))
        {
            this.AddPersonalPickup({
                ItemsIds: ticket.BasketList.map(x => x.Id),
                State: PersonalPickupStepState.PrePlanned,
                MeetingPlace: ticket.PickupPlace,
                PlannedDispatchTime: this.CalcDispatchTime(ticket),
                BasketSum: 0,
                DepositSum: 0,
                Training: ticket.WithTraining,
                CustomerInfo: this.CustomerInfo(ticket),
            });
        }
        else if (this.IsPackageDelivery(ticket))
        {
            this.AddPackageToCustomer(ticket, {
                PaymentAtDelivery: 0,
            });

            if (ticket.WithTraining)
            {
                this.AddTraining({
                    State: TrainingStepState.Planned,
                    PlannedTime: this.TrainingTime(ticket),
                });
            }
        }
        else throw new Error(`Unknown delivery form "${ticket.Delivery}"`);

        this.UsageAndReturnWhenDepositPaidWithTransfer(ticket);
    }

    private WhenDepositWithTransferAndBasketWithCash(ticket: Ticket): void
    {
        this.Log("Deposit with Transfer and Basket with Cash");

        this.AddPayment(ticket, {
            Category: PaymentCategory.Deposit,
            Title: this.TicketId(ticket),
            Amount: this.DepositSum(ticket),
            Form: ticket.DepositPaymentForm,
            Deadline: this.CalcPaymentDeadline(ticket),
        });

        if (this.IsPersonalDelivery(ticket))
        {
            this.AddPersonalPickup({
                ItemsIds: ticket.BasketList.map(x => x.Id),
                State: PersonalPickupStepState.PrePlanned,
                MeetingPlace: ticket.PickupPlace,
                PlannedDispatchTime: this.CalcDispatchTime(ticket),
                BasketSum: this.BasketSum(ticket),
                DepositSum: 0,
                Training: ticket.WithTraining,
                CustomerInfo: this.CustomerInfo(ticket),
            });
        }
        else if (this.IsPackageDelivery(ticket))
        {
            this.AddPackageToCustomer(ticket, {
                PaymentAtDelivery: this.BasketSum(ticket),
            });

            if (ticket.WithTraining)
            {
                this.AddTraining({
                    State: TrainingStepState.Planned,
                    PlannedTime: this.TrainingTime(ticket),
                });
            }
        }

        this.UsageAndReturnWhenDepositPaidWithTransfer(ticket);

        if (this.IsBasketWithCash(ticket) || this.IsBasketWithCashAtDelivery(ticket))
        {
            this.AddManaPostalDelivery({
                DeliveryDeadline: moment(this.CalcDispatchTime(ticket)).add(14, 'day').toDate(),
                Amount: this.BasketSum(ticket),
            });
        }
    }

    private UsageAndReturnWhenDepositPaidWithTransfer(ticket: Ticket)
    {
        this.AddUsage(ticket);

        if (this.IsPersonalReturn(ticket))
        {
            this.AddPersonalReturn({
                State: PersonalReturnStepState.Planned,
                MeetingPlace: ticket.PickupPlace,
                PlannedMeetingTime: moment(this.EndOfRent(ticket)).add(1, 'day').hour(0).minute(0).second(0).millisecond(0).toDate(),
                CashToReturn: 0,
            });
        }
        else if (this.IsPackageReturn(ticket))
        {
            this.AddPackageFromCustomer({
                State: PackageFromCustomerStepState.ReturnInfo,
                PlannedDispatchTime: moment(this.EndOfRent(ticket)).add(1, 'day').hour(12).minute(0).second(0).millisecond(0).toDate(),
            });
        }

        this.AddPaymentReturn(ticket, {
            Category: PaymentCategory.Deposit,
            AccountNumber: ticket.Customer.BankAccountNumber,
            Form: this.DepositPaymentReturnForm(ticket),
            Amount: this.DepositSum(ticket),
            Title: `Zwrot #${this.TicketId(ticket)}`,
            Deadline: moment(this.EndOfRent(ticket)).add(7, 'days').toDate(),
        });
    }

    private IsPackageDelivery(ticket: Ticket): boolean
    {
        return ticket.Delivery === DeliveryForm.Package;
    }

    private IsBasketWithCash(ticket: Ticket): boolean
    {
        return ticket.BasketPaymentForm === PaymentForm.Cash;
    }

    private WhenDepositWithCashAndBasketWithCash(ticket: Ticket)
    {
        // 🐤 Odbiór osobisty
        if (this.IsPersonalDelivery(ticket))
        {
            this.AddPersonalPickup({
                ItemsIds: ticket.BasketList.map(x => x.Id),
                State: PersonalPickupStepState.PrePlanned,
                MeetingPlace: ticket.PickupPlace,
                PlannedDispatchTime: this.CalcDispatchTime(ticket),
                BasketSum: this.BasketSum(ticket),
                DepositSum: this.DepositSum(ticket),
                Training: ticket.WithTraining,
                CustomerInfo: this.CustomerInfo(ticket),
            });

            // 🚀 Użytkowanie
            this.AddUsage(ticket);

            // 🐤 Zwrot osobisty
            if (this.IsPersonalReturn(ticket))
            {
                this.AddPersonalReturn({
                    State: PersonalReturnStepState.Planned,
                    MeetingPlace: ticket.PickupPlace,
                    PlannedMeetingTime: moment(this.EndOfRent(ticket)).add(1, 'day').hour(0).minute(0).second(0).millisecond(0).toDate(),
                    CashToReturn: this.DepositSum(ticket),
                });
            }
            // 📦 Zwrot paczką
            else if (this.IsPackageReturn(ticket))
            {
                this.AddPackageFromCustomer({
                    State: PackageFromCustomerStepState.ReturnInfo,
                    PlannedDispatchTime: moment(this.EndOfRent(ticket)).add(1, 'day').hour(12).minute(0).second(0).millisecond(0).toDate(),
                });

                this.AddPaymentReturn(ticket, {
                    Category: PaymentCategory.Deposit,
                    AccountNumber: ticket.Customer.BankAccountNumber,
                    Form: this.DepositPaymentReturnForm(ticket),
                    Amount: this.DepositSum(ticket),
                    Title: `Zwrot ${this.TicketId(ticket)}`,
                    Deadline: moment(this.EndOfRent(ticket)).add(7, 'days').toDate(),
                });

                this.AddManaPostalDelivery({
                    DeliveryDeadline: moment(this.CalcDispatchTime(ticket)).add(14, 'day').toDate(),
                    Amount: this.BasketSum(ticket),
                });
            }
        }
        else 
        {
            throw new Error("You can't use delivery when deposit is paid with cash");
        }
    }

    private DepositPaymentReturnForm(ticket: Ticket)
    {
        let depositReturnForm: PaymentForm;
        switch (ticket.DepositPaymentForm)
        {
            case PaymentForm.Cash:
            case PaymentForm.CashAtDelivery:
            case PaymentForm.TransferAtDelivery:
                depositReturnForm = PaymentForm.PostalTransfer; // <-- this is important
                break;
            case PaymentForm.BankTransfer:
                depositReturnForm = PaymentForm.BankTransfer;
                break;
            case PaymentForm.Blik:
                depositReturnForm = PaymentForm.Blik;
                break;
            case PaymentForm.Crypto:
                depositReturnForm = PaymentForm.Crypto;
                break;
            default:
                throw new Error(`Can not determine deposit return form for "${ticket.DepositPaymentForm}" deposit payment form`);
        }
        return depositReturnForm;
    }

    private CustomerInfo(ticket: Ticket): string
    {
        const expIcon = { 0: "🐤", 1: "🥔", 2: "🥉", 3: "🥈", 4: "🥇" };
        const exp = expIcon[ticket.Customer.Experience || 0];

        return `${exp} ${ticket.Customer.Name}, tel. ${TextFormatter.NicePhoneNumber(ticket.Customer.Phone)}`;
    }

    private IsPersonalReturn(ticket: Ticket): boolean
    {
        return ticket.Return == ReturnForm.Personal;
    }

    private IsPackageReturn(ticket: Ticket): boolean
    {
        return ticket.Return == ReturnForm.Package;
    }

    private IsDepositTransferable(ticket: Ticket)
    {
        return this.IsTransferable(ticket.DepositPaymentForm);
    }

    private IsDepositWithCash(ticket: Ticket)
    {
        return ticket.DepositPaymentForm === PaymentForm.Cash;
    }

    private TicketId(ticket: Ticket)
    {
        return ticket.Id.slice(-5).toUpperCase();
    }

    private IsPersonalDelivery(ticket: Ticket)
    {
        return ticket.Delivery === DeliveryForm.Personal;
    }

    private DeliveryAddress(ticket: Ticket): string
    {
        return `🧑‍💼 ${ticket.Customer.Name}\n🗺️ ${ticket.Customer.Address}\n☎️ ${TextFormatter.NicePhoneNumber(ticket.Customer.Phone)}`;
    }

    private AddStep(step: Step<any>): void
    {
        this.Log(`Adding step ${step.Type}`);
        this.steps.Add(step);
    }

    private AddAddToBasket(basket: Partial<AddToBasketStepRawData>): void
    {
        this.steps.Add(this._factory.Create(StepType.AddToBasket, basket as AddToBasketStepRawData));
    }

    private AddPersonalPickup(personal: Partial<PersonalPickupStepRawData>): void
    {
        personal.PlannedDispatchTime = moment(personal.PlannedDispatchTime).hour(0).minutes(0).seconds(0).milliseconds(0).toDate();

        this.AddStep(this._factory.Create(StepType.PersonalPickup, personal as PersonalPickupStepRawData));
    }

    private AddPackageToCustomer(ticket: Ticket, transit: Partial<PackageToCustomerStepRawData>): void
    {
        transit.State = PackageToCustomerStepState.Planned;
        transit.PackageContent = ticket.BasketList.map(x => x.Id);
        transit.PlannedDispatchTime = this.CalcDispatchTime(ticket);
        transit.AddressFullInfo = this.DeliveryAddress(ticket);

        this.AddStep(this._factory.Create(StepType.PackageToCustomer, transit as PackageToCustomerStepRawData));
    }

    private AddTraining(training: Partial<TrainingStepRawData>): void
    {
        this.AddStep(this._factory.Create(StepType.Training, training as TrainingStepRawData));
    }

    private AddPayment(ticket: Ticket, payment: Partial<PaymentStepRawData>): void
    {
        if (payment.Amount && payment.Amount > 0)
        {
            payment.Currency = "PLN";
            payment.PaymentCurrency = ticket.DepositPaymentForm == PaymentForm.Crypto ? "BTC" : "PLN";
            payment.ExchangeRate = this.btcValueInPln;

            this.AddStep(this._factory.Create(StepType.Payment, payment as PaymentStepRawData));
        }
        else this.Log(`Payment amount is 0. Omitting payment step`);
    }

    private AddInvoice(invoice: Partial<InvoiceStepRawData>): void
    {
        this.AddStep(this._factory.Create(StepType.Invoice, invoice as InvoiceStepRawData));
    }

    private AddUsage(ticket: Ticket): void
    {
        this.AddStep(this._factory.Create(StepType.Usage, {
            State: UsageStepState.BeforeStart,
            Start: this.StartOfRent(ticket),
            End: this.EndOfRent(ticket),
        } as UsageStepRawData));
    }

    private AddPersonalReturn(personal: Partial<PersonalReturnStepRawData>): void
    {
        this.AddStep(this._factory.Create(StepType.PersonalReturn, personal as PersonalReturnStepRawData));
    }

    private AddPackageFromCustomer(transit: Partial<PackageFromCustomerStepRawData>): void
    {
        this.AddStep(this._factory.Create(StepType.PackageFromCustomer, transit as PackageFromCustomerStepRawData));
    }

    private AddPaymentReturn(ticket: Ticket, payment: Partial<PaymentStepRawData>): void
    {
        payment.Currency = "PLN";
        payment.PaymentCurrency = ticket.DepositPaymentForm == PaymentForm.Crypto ? "BTC" : "PLN";
        payment.ExchangeRate = this.btcValueInPln;

        this.AddStep(this._factory.Create(StepType.PaymentReturn, payment as PaymentStepRawData));
    }

    private AddManaPostalDelivery(payment: Partial<ManaPostalDeliveryStepRawData>): void
    {
        payment.VisibleForCustomer = false;

        this.AddStep(this._factory.Create(StepType.ManaPostalDelivery, payment as ManaPostalDeliveryStepRawData));
    }

    private BasketSum(ticket: Ticket): number
    {
        return ticket.BasketList.reduce((sum, item) => sum + item.TotalPrice, 0);
    }

    private IsAnythingToRent(ticket: Ticket): boolean
    {
        return ticket.BasketList.some(x => x.Type == BasketItemType.Rent);
    }

    private StartOfRent(ticket: Ticket): Date | undefined
    {
        const rent = ticket.BasketList.find(x => x.Type == BasketItemType.Rent) as RawRentBasketItem;
        if (!rent) return undefined;
        return moment(rent.From).hour(0).minutes(1).seconds(0).milliseconds(0).toDate();
    }

    private TrainingTime(ticket: Ticket): Date | undefined
    {
        const start = moment(this.StartOfRent(ticket));
        return start.hour(0).minutes(0).seconds(0).milliseconds(0).toDate();
    }

    private EndOfRent(ticket: Ticket): Date | undefined
    {
        const rent = ticket.BasketList.find(x => x.Type == BasketItemType.Rent) as RawRentBasketItem;
        if (!rent) return undefined;
        return moment(rent.To).hour(23).minutes(59).seconds(0).milliseconds(0).toDate();
    }

    private DepositSum(ticket: Ticket): number
    {
        return ticket.BasketList.reduce((sum, item) => sum + ((item as RawRentBasketItem).Deposit || 0), 0);
    }

    private CalcPaymentDeadline(ticket: Ticket): Date
    {
        const dispatchTime = this.CalcDispatchTime(ticket);
        const prefered = moment(dispatchTime).subtract(1, 'day').hour(0).minutes(0).seconds(0).milliseconds(0).toDate();
        const now = DateTimeProvider.Now;
        if (prefered < now)
        {
            return now;
        }
        return prefered;
    }

    private CalcDispatchTime(ticket: Ticket): Date
    {
        let plannedDispatch = new Date(0);

        const startOfRent = this.StartOfRent(ticket);

        if (startOfRent) // Is anything to rent
        {
            const threshold = this.IsPersonalDelivery(ticket) ? 1 : 4;

            plannedDispatch = DispatchTimeCalculator.GetDispatchTime(startOfRent, threshold);
        }
        else // Sale only
        {
            // Dispatch tomorrow
            plannedDispatch = moment(DateTimeProvider.Now).add(1, 'day').hour(12).minutes(0).seconds(0).milliseconds(0).toDate();
        }

        return moment(plannedDispatch).hour(12).minutes(0).seconds(0).milliseconds(0).toDate();
    }

    private IsTransferable(form: PaymentForm): boolean
    {
        return [PaymentForm.BankTransfer, PaymentForm.Crypto, PaymentForm.Blik].includes(form);
    }
}
